import React, { useState, useEffect } from 'react';
import { FaCalculator, FaCheckCircle, FaCode, FaFile } from 'react-icons/fa';
import printHTML, { downloadText } from '../helpers';
import ReactGA from "react-ga4";

const MultiChoice = (props: { index: number, question: string, answers: string[], onSelect: (index: number, response: string) => void, attemptedSubmission: boolean }) => {
  const [selected, setSelected] = useState<string | null>(null);

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelected(value);
    props.onSelect(props.index, value);
  };

  useEffect(() => {
    if (props.attemptedSubmission && !selected) {
      // Highlight the unanswered question in red
      document.getElementById(`multi-choice-${props.index}`)?.classList.add('unanswered');
    } else {
      document.getElementById(`multi-choice-${props.index}`)?.classList.remove('unanswered');
    }

    ReactGA.event({
      category: "TAPPForm",
      action: "attempted_incomplete_scoring",
    });
  }, [props.attemptedSubmission, selected, props.index]);

  return (
    <div id={`multi-choice-${props.index}`} className="multi-choice" style={{ marginBottom: "10px" }}>
      <p className="question">{props.question}</p>
      <ul className="answers-list">
        {props.answers.map((answer, index) => (
          <li key={index} className="answer-option">
            <label>
              <input
                type="radio"
                name={`multi-choice-${props.index}`}
                value={answer}
                onChange={handleSelect}
              />
              {answer}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
};

const TableSelect = (props: { index: number, questions: string[], columns?: string[], onSelect: (index: number, response: { [key: string]: string | boolean }) => void, attemptedSubmission: boolean }) => {
  const columns = props.columns ? props.columns : ["Yes", "No", "Unsure"];
  const [responseData, setResponseData] = useState<{ [key: string]: string | boolean }>({});

  const questionColumnWidth = 300;  // Width for the question column
  const responseColumnWidth = 75;  // Width for each response column

  const handleClick = (question: string, column: string) => {
    const updatedResponses = {
      ...responseData,
      [question]: column,
    };
    
    setResponseData(updatedResponses);
    props.onSelect(props.index, updatedResponses);
  };

  useEffect(() => {
    // Loop through each question to check if it's unanswered and apply class to the specific row
    props.questions.forEach((question, questionIndex) => {
      const rowElement = document.getElementById(`table-select-${props.index}-row-${questionIndex}`);
      if (props.attemptedSubmission && !responseData[question]) {
        rowElement?.classList.add('unanswered');
      } else {
        rowElement?.classList.remove('unanswered');
      }
    });
  }, [props.attemptedSubmission, responseData, props.index, props.questions]);

  return (
    <table id={`table-select-${props.index}`} border={1} cellPadding={10} style={{ marginBottom: "15px" }}>
      <thead>
        <tr>
          <th style={{ width: questionColumnWidth + "px" }}>Question</th>
          {columns.map((column) => (
            <th key={column} style={{ width: responseColumnWidth + "px" }}>{column}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {props.questions.map((question, questionIndex) => (
          <tr id={`table-select-${props.index}-row-${questionIndex}`} key={question}>
            <td style={{ width: questionColumnWidth + "px" }}>{question}</td>
            {columns.map((column) => (
              <td
                key={`${question}-${column}`}
                onClick={() => handleClick(question, column)}
                style={{ width: responseColumnWidth + "px", cursor: 'pointer', textAlign: 'center' }}
              >
                {responseData[question] === column ? <FaCheckCircle style={{ color: 'var(--primary-accent-color)', fontSize: '1.5rem' }} /> : ''}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};


export function TAPPForm() {
  const [formData, setFormData] = useState<{ [key: number]: any }>({});
  const [finalScore, setFinalScore] = useState<null | number>(null);
  const [attemptedSubmission, setAttemptedSubmission] = useState(false);

  const handleSelection = (index: number, response: string | { [key: string]: string | boolean }) => {
    let newFormData = { ...formData };
    newFormData[index] = response;
    setFormData(newFormData);
  };

  const isFormComplete = () => {
    const TOTAL_QUESTIONS = 21;
    if (Object.keys(formData).length < TOTAL_QUESTIONS) return false;

    for (const key in formData) {
      const value = formData[key];
      if (typeof value === 'object') {
        if (value.complete) {
          return false;
        };
      }
    }

    return true;
  };

  const handleCalculateScore = () => {
    setAttemptedSubmission(true);
    if (isFormComplete()) {
      let score = 0;

      // Tally up points
      for (const question in formData) {
        const value = formData[question];

        // Specific scoring rules
        if (question === "4") {
          if (['Never/Rarely', 'Sometimes', 'Usually'].includes(value)) {
            score += 0;
          } else {
            score += 1;
          }
        } else if (question === "8") {
          if (["No", "Not Applicable/Solo PCP", "Don’t Know/Unsure"].includes(value)) {
            score += 0;
          } else {
            score += 1;
          }
        } else if (question == "9") {
          if (value.includes("Yes")) score += 1;
        }
        else if (question === "11") {
          if (["No", "Don’t Know/Unsure", "Not Applicable, practice does not have care coordinators"].includes(value)) {
            score += 0;
          } else {
            score += 1;
          }
        } else {
          // Multichoice or Table
          if (typeof value === 'string') {
            if (value === "Yes") score += 1;
          } else if (typeof value === 'object') {
            for (const key in value) {
              if (value[key] === "Yes") score += 1;
            }
          }
        }
      }

      ReactGA.event({
        category: "TAPPForm",
        action: "scored_successfully",
      });

      setFinalScore(score);
    }
  };

  return(
    <div>
    <div id='TAPPForm'>
        <b>The first set of questions is about access to care at this practice.</b>
        <p>1. Please indicate which of the following are true of your practice:</p>
        <TableSelect index={1} onSelect={handleSelection} attemptedSubmission={attemptedSubmission} questions={[
            `a. PCPs (MD, DO, NP, PA) are available outside of
the practice’s regular office hours to respond to
patients who call`,
            `b. Practice is open outside of typical weekday hours
(e.g., 9-5)`,
            `c. Practice offers same day and/or walk-in
appointments`,
            `d. Practice offers group visits/shared medical
appointments with multiple patients`,
            `e. PCPs conduct in-home visits`,
            `f. Staff other than PCPs (e.g., nurses, social workers,
community health workers) conduct in-home visits`,
            `g. Practice offers point-of-care testing (i.e., onsite)
for certain lab tests`,
            `h. Practice offers scheduled multidisciplinary visits
onsite (or virtual) for patients (e.g., PCP and specialist meet jointly with patient; PCP and case
manager meet jointly with patient)`,
            `i. Practice offers option for longer scheduled
appointments based on patient need`,
            `j. Practice uses a standardized telephone triage
assessment to inform patient if and where to seek
care`,
            `k. Patients have online access to their medical
records`     
            ]}/>
        <b>The next section explores the various ways this practice communicates with patients.</b>
        <p>2. Please indicate which of the following your practice uses to communicate with or monitor
        patients:</p>
        <TableSelect index={2} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
        questions={[
            "a. Patient portal to track self-management goals",
            "b. Patient portal to communicate with patients",
            "c. Bidirectional communication with patients via text or email"
          ]}
        />
        <b>The following set of questions identifies any screens <u><b>routinely</b></u> utilized by the practice.</b>
        <p>3. Please indicate for which of the following the practice uses formal tools to routinely screen all
        patients:</p>
        <TableSelect index={3} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
        questions={[
            "a. Alcohol use",
            "b. Drug use",
            "c. Tobacco use",
            "d. Mental health symptoms",
            "e. Health literacy",
            "f. Social needs or social determinants of health (e.g., food insecurity, legal problems, at-risk for homelessness)"
          ]}/>
          <b>The next set of questions is about patients’ experiences with care.</b>
          <MultiChoice index={4} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
          question={'4. In this practice, how often do patients see the same PCP (other than vacation time)?'} answers={[
            "Never/Rarely",
            "Sometimes",
            "Usually",
            "Always",
            "Not Applicable/Solo PCP"
            ]
        }/>
        <MultiChoice index={5} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
        question={"5. Does the practice offer to include family members in shared decision-making conversations?"}
        answers={[
            "Yes",
            "No",
            "Don’t Know/Unsure"
          ]}/>
        <MultiChoice index={6} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
        question={"6. Does the practice have onsite PCPs or staff that speak the most dominant, non-English language spoken by patients?"}
        answers={[
            "Yes",
            "No",
            "Don’t Know/Unsure"
          ]}/>
        <b>The following question is about this practice’s requirements for formal training.</b>
        <p>7. Please indicate which of the following are required formal trainings for providers:</p>
        <TableSelect index={7} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
        questions={[
            "a. Cultural competency",
            "b. Implicit bias",
            "c. Motivational interviewing"
          ]
          }/>
        <b>The next section is about this practice’s care team(s) including its/their makeup and
        function.</b>
        <MultiChoice index={8} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
        question={"8. Does this practice divide patients into panels that are assigned to specific PCPs and/or care teams?"}
        answers={[
            "Yes",
            "No",
            "Don’t Know/Unsure",
            "Not Applicable/Solo PCP"
          ]}/>
        <MultiChoice index={9} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
        question={"9. Does this practice have designated staff to manage patient panels?"}
        answers={[
            "Yes, medical assistants act as panel managers",
            "Yes, PCPs act as panel managers",
            "Yes, other staff act as panel managers",
            "No, no staff dedicated to panel management",
            "Not Applicable, practice does not maintain patient panels"
          ]
          }/>
        <p>10. Please indicate the types of people who collaborate to provide care for patients in this
practice other than PCPs. Note: These individuals do not need to be onsite or employed by the
primary care practice.</p>
        <TableSelect index={10} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
        questions={[
            "a. Medical assistant",
            "b. Clinicians other than PCPs (e.g., RN, LPN)",
            "c. Trained health coach",
            "d. Paraprofessionals/CHWs",
            "e. Care coordinator",
            "f. Nutritionist/dietitian",
            "g. Pharmacist",
            "h. Behavioral/mental health provider"
          ]}/>
        <MultiChoice index={11} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
        question={"11. Does this practice facilitate regular communication between PCPs and care coordinators (e.g., regularly scheduled meetings, communication template)?"}
        answers={[
            "Yes",
            "No",
            "Don’t Know/Unsure",
            "Not Applicable, practice does not have care coordinators"
          ]}/>
        <p>12. Please read through each item and indicate your response:</p>
        <TableSelect index={12} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
        questions={[
            "a. Practice regularly reviews roles and responsibilities with all PCPs and staff",
            "b. Medical assistants in this practice are responsible for tasks other than taking vitals (e.g., providing patient education; conducting screens, etc.)"
          ]
          }
        columns={["Yes", "No"]}/>
        <b>The following set of questions elicit any <u>non-clinical</u> patient services that this practice
        offers.</b>
        <p>13. When needed, does this practice <u><b>routinely:</b></u></p>
        <TableSelect index={13} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
        questions={[
            "a. Assist patients with insurance-related issues (e.g., enrollment, getting approvals for procedures)",
            "b. Help patients determine benefit eligibility",
            "c. Provide support to patients on where to access lower-cost medications",
            "d. Refer patients to community resources outside of the office based on a social needs screening",
            "e. Arrange free or low-cost transportation services to and from the clinic",
            "f. Make referral appointments for patients",
            "g. Refer at-risk patients to staff for lifestyle promotion (e.g., exercise, diet)",
            "h. Help vulnerable patients navigate the health care system (e.g., make appointments for lab and radiology tests)"
          ]
          }/>
          <b>The next section concerns how this practice engages with patients during visits.</b>
          <p>14. Does this practice:</p>
          <TableSelect index={14} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
          questions={[
            "a. Direct PCPs to assess and review patient goals pre-visit",
            "b. Provide all patients ages 40-79 with a cardiovascular disease (CVD) risk score during their visit",
            "c. Provide patients with clinical summaries after each of their visits"
          ]
          }/>
          <b>The next set of questions concerns care coordination (within the practice and externally).</b>
          <p>15. Please indicate whether this practice <u><b>routinely:</b></u></p>
          <TableSelect index={15} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
          questions={[
            "a. Identifies and contacts patients with chronic illnesses about unmet chronic care needs (e.g., poorly controlled diabetes or blood pressure)",
            "b. Reminds patients of upcoming appointments",
            "c. Follows up with patients who have cancelled or missed appointments",
            "d. Uses patient-initiated contacts (e.g., medication refill request) to reassess patients’ other needs (e.g., need for follow-up appointment)",
            "e. Schedules follow-up appointments before patients leave office",
            "f. Identifies and contacts patients about preventive care needs (e.g., vaccines and screenings)",
            "g. Generates lists of patients by specific conditions (e.g., patient registry)",
            "h. Monitors high-risk populations with registries or other systematic methods",
            "i. Provides clinical care management for high-risk patients",
            "j. Uses pre-visit planning data to identify patients to meet with care managers",
            "k. Performs medication reconciliation",
            "l. Allows someone in the practice who is not the patient’s PCP to change medications"
          ]
          }/>
          <p>16. Does this practice:</p>
          <TableSelect index={16} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
          questions={[
            "a. Contract with external organizations to provide comprehensive care management for medically complex patients",
            "b. Contract with a behavioral health organization",
            "c. Facilitate direct communication between PCPs and specialists to whom they routinely refer (e.g., shared EHRs, co-location)",
            "d. Facilitate bidirectional communication between PCPs and mental health specialists (e.g., shared EHRs, co-location)",
            "e. Have a system in place to receive clinical information (i.e., assessment and treatment plan) from specialty referrals",
            "f. Refer patients to in-house specialists (e.g., cardiologists)",
            "g. Schedule routine visits with a pharmacist for patients",
            "h. Have processes in place to effectively manage care transitions to and from hospital and emergency department (e.g., practice uses protocols for scheduling timely visit after hospital/emergency department discharge)",
            "i. Work with inpatient case managers to identify high-risk patients who need follow-up support",
            "j. Use the EHR to view patient visits to other clinicians (e.g., specialists at other institutions/practices)"
          ]
          }/>
          <b>The following section queries this practice’s clinical information systems.</b>
          <p>17. Which of the following does the practice have in place?</p>
          <TableSelect index={17} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
          questions={[
            "a. Template/customized order sets for frequently used physician orders",
            "b. EHR-based clinical decision tool has built-in alerts for drug interactions",
            "c. Clinical decision support tool that allows PCPs to directly order diagnostic tests, medication, and counseling",
            "d. Reminders or best-practice alerts for services that a patient should receive",
            "e. A single EHR alert that includes all tests for which the patient was eligible at the time of visit and automatically linked to a preapproved order set",
            "f. An online or automated call-in system where patients can renew medications",
            "g. Computer-based order entry for prescriptions, procedures, or lab work",
            "h. EHR-generated alerts for non-formulary medication choices",
            "i. Social history template in patient encounters (e.g., living situation, family configuration)",
            "j. Structured templates for chronic disease management",
            "k. Requirement for PCPs to routinely update problem list in EHR",
            "l. Requirement for PCPs to assess and track medication adherence in EHR",
            "m. Standardized order sets"
          ]
          }/>
          <b>The next set of questions concerns this practice’s current experiences with telemedicine and
          remote monitoring.</b>
          <p>18. Please indicate which of the following are true of your practice:</p>
          <TableSelect index={18} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
          questions={[
            "a. Practice offers synchronous home-based telemedicine visits (e.g., video or telephone) with patients",
            "b. Practice provides patients with equipment (e.g., tablet) to use telemedicine from home",
            "c. Remote monitoring of patients by PCPs (e.g., ongoing monitoring of patient data, such as blood pressure, blood glucose, weight)",
            "d. Practice combines home-based monitoring (e.g., blood pressure, blood glucose) with nurse-administered behavioral interventions",
            "e. Practice equips patients with appropriate self-management tools (e.g., glucometers, blood pressure monitors)",
            "f. Practice provides or facilitates access to virtual interactive modules for patients to learn problem-solving, self-management and/or related skills",
            "g. Practice provides or facilitates access to online tools or devices for patients to track self-management goals"
          ]
          }/>
          <b>The following set of questions concerns the types of ancillary clinical services that are
          provided onsite at this practice.</b>
          <p>19. Please indicate which of the following ancillary services are available onsite (i.e., not
            provided by a PCP):</p>
            <TableSelect index={19} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
            questions={[
                "a. Nutrition counseling/diabetes education",
                "b. Mental/behavioral health services",
                "c. Social work services",
                "d. Health coaching",
                "e. Dental services"
              ]
              }/>
         <b>The next section is about the types of meetings that this practice holds.</b>
         <p>20. Which of the following meetings does your practice <u><b>routinely</b></u> have?</p>
         <TableSelect index={20} onSelect={handleSelection} attemptedSubmission={attemptedSubmission}
         questions={[
            "a. Meetings between PCPs and staff to discuss patients and their care",
            "b. Position/role specific meetings (e.g., all MAs, all RNs, all administrators, all providers)",
            "c. Daily in-person or virtual 'huddle' meetings",
            "d. All-staff meetings"
          ]
          }/>
          <b>The following set of questions is about this practice’s quality improvement activities.</b>
          <p>21. Please indicate which of the following are <b><u>routine</u></b> activities in your practice:</p>
          <TableSelect index={21} onSelect={handleSelection} attemptedSubmission={attemptedSubmission} questions={[
  "a. Set performance goals for the practice",
  "b. Track quality of care for the practice against performance goals",
  "c. Provide PCPs with individual performance feedback specific to their patient panel",
  "d. Build quality improvement activities into practice operations",
  "e. Dedicate time to reflect and evaluate following a practice change project (e.g., identify lessons learned, debrief)",
  "f. Apply process improvement methodologies (e.g., Lean principles, plan-do-study-act cycles)",
  "g. Provide protected time and resources to dedicated quality improvement team",
  "h. Have a system in place for data quality assurance (e.g., identify inaccuracies in their data)",
  "i. Use a visual dashboard that displays clinical quality measures to manage patients with chronic disease",
  "j. Use a visual dashboard that displays clinical quality measures to manage preventive care for patients",
  "k. Customize quality reports by patient characteristics (e.g., demographics, diagnosis)",
  "l. Actively track new clinical care guidelines and incorporate them into the practice"
]
}/>
{attemptedSubmission ? (
  <div>
    <p style={{marginLeft: "10px"}}>
      <strong>{finalScore === null ? "Answer all questions to score the TAPP" : "Score: " + finalScore}</strong>
    </p>
  </div>
) : null}

</div>
<button className="majorButton" onClick={handleCalculateScore} disabled={false}><FaCalculator style={{marginRight: "3px", marginBottom: "-1px"}}/>Calculate Score</button>
{(attemptedSubmission && finalScore !== null) ? (
  <div style={{display: "flex", marginTop: "5px"}}>
    <button className='minorButton' style={{marginRight: "5px"}} onClick={() => {
      printHTML("TAPPForm");
      ReactGA.event({
        category: "TAPPForm",
        action: "print_results",
      });
    }}><FaFile style={{marginRight: "3px", marginBottom: "-1px"}}/>Download PDF</button>
    <button className='minorButton' onClick={() => {
      downloadText("TAPP-data.json", JSON.stringify({
        content: formData,
        score: finalScore
      }));
      ReactGA.event({
        category: "TAPPForm",
        action: "download_results",
      });
    }}><FaCode style={{marginRight: "3px", marginBottom: "-1px"}}/>Download JSON</button>
  </div>
) : null}
</div>);
}